import { navigate } from '@reach/router'
import { NextRouter } from 'next/router'
import { arrayToObject } from '~/common/arrayUtils'
import { isNextJsPage } from '~/common/nextJsUtils'
import {
  CalculatedField,
  CustomFieldPath,
  DatasetField,
  FieldSource,
  Formula,
  PathAndField,
  QueryFilter,
  ReportDataTypes,
  ReportField,
  ReportQuery,
  SortDirectionType
} from '~/custom_reports/types'
import { FormulaConfig } from '~/custom_reports/v3/calculated_fields/FormulaConfig'
import { ifMatchThen } from '~/custom_reports/v3/utils'

interface SimpleRouter {
  push(path: string): void
}

interface useMultiRouterProps {
  nextRouter: NextRouter
  webpackRouter?: SimpleRouter
  externalRouter?: SimpleRouter
}

const baseWebpackRouter: SimpleRouter = {
  push: path => {
    navigate(path)
  }
}

const baseExternalRouter: SimpleRouter = {
  push: path => {
    window.location.href = path
  }
}

export const useMultiRouter = ({
  nextRouter,
  webpackRouter = baseWebpackRouter,
  externalRouter = baseExternalRouter
}: useMultiRouterProps) => ({
  push: (path: string, isNextPageNextJs: boolean) => {
    if (isNextJsPage()) {
      nextRouter.push(path)
    } else if (isNextPageNextJs) {
      externalRouter.push(path)
    } else {
      webpackRouter.push(path)
    }
  }
})

/* TODO: Remove when we can update to @chakra-ui/react@1.6.0 * This is a work around to allow typing in
 * the search bar as currently * Chakra's menulist doesn't support text inputs
 * https://github.com/chakra-ui/chakra-ui/issues/3697
 */
type KeyDownEventType = {
  key: string
  stopPropagation: () => void
}

export const onKeyDownHandler = (e: KeyDownEventType) => {
  if (!['ArrowUp', 'ArrowDown', 'Escape'].includes(e.key)) {
    e.stopPropagation()
  }
}

export const getSourceTypeMessageToken = (
  f: PathAndField,
  fieldsByPath?: Record<string, Record<string, ReportField>>,
  fieldSourcesLookup?: Record<string, FieldSource>
): string => {
  enum FieldSourceTypeToken {
    CalculatedField = 'calculated_field',
    Field = 'field',
    Parameter = 'parameter',
    SystemVariable = 'system_variable',
    FixedValue = 'fixed_value',
    SpecificiedValue = 'fixed_value'
  }

  const _getSourceTypeMessageToken = (path: string, field: ReportField | null): FieldSourceTypeToken | null => {
    switch (path) {
      case CustomFieldPath.Calculated:
        if ((field as CalculatedField)?.formula === Formula.Value) {
          return FieldSourceTypeToken.FixedValue
        }
        return FieldSourceTypeToken.CalculatedField
      case CustomFieldPath.Parameter:
        return FieldSourceTypeToken.Parameter
      case CustomFieldPath.SystemVariable:
        return FieldSourceTypeToken.SystemVariable
      case CustomFieldPath.Value:
        return FieldSourceTypeToken.FixedValue
      case CustomFieldPath.SpecifiedValue:
        return FieldSourceTypeToken.SpecificiedValue
      default:
        return null
    }
  }

  if (!(f.field && f.path)) {
    return ''
  }

  const fieldDef = (fieldsByPath && fieldsByPath[f.path] && fieldsByPath[f.path][f.field]) ?? null
  let partial = _getSourceTypeMessageToken(f.path, fieldDef)

  // For Dataset Fields, check if there's a field source, if not, fallback to "Field"
  const fieldSource = (fieldSourcesLookup && fieldSourcesLookup[f.path]) ?? null
  partial = partial ?? (fieldSource ? null : FieldSourceTypeToken.Field)

  return partial ? `custom_report.field_source_type.${partial}` : fieldSource?.display_name ?? ''
}

export const getSortDirectionMessageToken = (sortDirectionType: SortDirectionType): string =>
  sortDirectionType === SortDirectionType.Ascending
    ? 'invoice_billing.sort_ascending'
    : 'invoice_billing.sort_descending'

const matchCopyReportTitle = / - Copy(?: \((\d+)\))?$/g
export const getCopyMatchDetails = (reportTitle: string): { match: string | null; count: number } => {
  const match = reportTitle.matchAll(matchCopyReportTitle).next()
  if (!match.value) {
    return { match: null, count: 0 }
  }
  const [copyMatch, countMatch] = match.value
  if (!copyMatch) {
    return { match: null, count: 0 }
  }

  return { match: copyMatch, count: Number(countMatch ?? 1) }
}

/**
 * Givem a map of strings to objects, remap based off the selector function
 * @param map the map of strings to objects
 * @param selector function to remap the objects
 * @returns a remapped object
 */
export const mapObject = <T, U>(map: Record<string, T>, selector: (obj: T) => Record<string, U>) =>
  arrayToObject(
    Object.keys(map),
    key => key,
    key => selector(map[key])
  )

export const buildQuery = (query: ReportQuery, viewFilters: QueryFilter[] = []) => ({
  ...query,
  viewFilters
})

export const isFieldFilterable = (field: ReportField) => {
  const basicFilterableTypes = [
    ReportDataTypes.Integer,
    ReportDataTypes.String,
    ReportDataTypes.Date,
    ReportDataTypes.Datetime,
    ReportDataTypes.Boolean,
    ReportDataTypes.Html,
    ReportDataTypes.Number,
    ReportDataTypes.Time,
    ReportDataTypes.Text
  ]

  return (
    // Basic filterable types Filterable
    ifMatchThen<DatasetField>(field, field => Boolean(basicFilterableTypes.includes(field.type))) ||
    // Fields with filter_type defined should be filterable
    ifMatchThen<DatasetField>(field, field => Boolean(field.filter_type)) ||
    ifMatchThen<CalculatedField>(
      field,
      field => field.formula && !FormulaConfig[field.formula]?.usageLimitations?.filterFieldDisallowed
    )
  )
}
